import { Controller } from 'stimulus'

// The shared clients endpoint carries prospects via quote=1. A chosen client fills the `sites`
// target too, and every change of client is announced as `crm-client-search:picked`.
export default class extends Controller {
  static targets = ['input', 'hidden', 'dropdown', 'clear', 'sites']

  connect() {
    this._debounce = null
    this._outside = (e) => { if (!this.element.contains(e.target)) this._hide() }
    document.addEventListener('click', this._outside)
  }

  disconnect() {
    document.removeEventListener('click', this._outside)
  }

  onInput() {
    clearTimeout(this._debounce)
    // Typing invalidates any prior selection.
    if (this.hasHiddenTarget && this.hiddenTarget.value) {
      this.hiddenTarget.value = ''
      this._announce('')
    }
    if (this.hasClearTarget) this.clearTarget.classList.add('hidden')
    this._resetSites()
    this._debounce = setTimeout(() => this._fetch(), 200)
  }

  onKeydown(e) {
    if (e.key === 'Escape') this._hide()
  }

  clear() {
    this.inputTarget.value = ''
    if (this.hasHiddenTarget) this.hiddenTarget.value = ''
    if (this.hasClearTarget) this.clearTarget.classList.add('hidden')
    this._resetSites()
    this._announce('')
    this._hide()
    this.inputTarget.focus()
  }

  _fetch() {
    const q = this.inputTarget.value.trim()
    if (q.length < 1) { this._hide(); return }
    const url = new URL(this.data.get('url'), window.location.origin)
    url.searchParams.set('q', q)
    url.searchParams.set('quote', '1') // include prospects
    fetch(url, { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then((r) => r.json())
      .then((list) => this._render(Array.isArray(list) ? list : (list.clients || [])))
      .catch(() => {})
  }

  _render(clients) {
    if (!this.hasDropdownTarget) return
    if (!clients.length) {
      this.dropdownTarget.innerHTML = '<div class="px-3 py-2.5 text-[13px] text-slate-400">Aucun client trouvé.</div>'
      this._show()
      return
    }
    this.dropdownTarget.innerHTML = clients.map((c) => `
      <button type="button" data-action="crm-client-search#pick" data-id="${c.id}" data-name="${this._esc(c.name)}"
        class="w-full text-left px-3 py-2.5 text-[13.5px] text-slate-800 hover:bg-slate-50 flex items-center justify-between gap-2">
        <span class="truncate">${this._esc(c.name)}</span>
        ${c.prospect ? '<span class="shrink-0 text-[10px] font-medium text-amber-700 bg-amber-50 rounded-full px-1.5 py-0.5">Prospect</span>' : ''}
      </button>`).join('')
    this._show()
  }

  pick(e) {
    const btn = e.currentTarget
    this.inputTarget.value = btn.dataset.name
    if (this.hasHiddenTarget) this.hiddenTarget.value = btn.dataset.id
    if (this.hasClearTarget) this.clearTarget.classList.remove('hidden')
    this._loadSites(btn.dataset.id)
    this._announce(btn.dataset.id)
    this._hide()
  }

  _announce(clientId) {
    this.element.dispatchEvent(new CustomEvent('crm-client-search:picked', { detail: { clientId: clientId } }))
  }

  _resetSites() {
    if (!this.hasSitesTarget) return
    const prompt = this.sitesTarget.options[0]
    this.sitesTarget.innerHTML = ''
    if (prompt) this.sitesTarget.appendChild(prompt)
    this.sitesTarget.value = ''
  }

  _loadSites(clientId) {
    if (!this.hasSitesTarget) return
    const endpoint = this.data.get('sites-url')
    if (!endpoint || !clientId) { this._resetSites(); return }

    this._resetSites()
    const url = new URL(endpoint, window.location.origin)
    url.searchParams.set('client_id', clientId)
    const seq = (this._sitesSeq = (this._sitesSeq || 0) + 1)
    fetch(url, { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then((r) => r.json())
      .then((rows) => { if (seq === this._sitesSeq) this._renderSites(Array.isArray(rows) ? rows : []) })
      .catch(() => {})
  }

  _renderSites(rows) {
    rows.forEach((row) => {
      const option = document.createElement('option')
      option.value = row.id
      option.textContent = row.name
      this.sitesTarget.appendChild(option)
    })
    // One site is no choice at all: fill it rather than block the form on it.
    if (rows.length === 1) this.sitesTarget.value = rows[0].id
  }

  _show() { if (this.hasDropdownTarget) this.dropdownTarget.classList.remove('hidden') }
  _hide() { if (this.hasDropdownTarget) this.dropdownTarget.classList.add('hidden') }

  _esc(s) { const d = document.createElement('div'); d.textContent = s == null ? '' : s; return d.innerHTML }
}
